import React from "react";
import * as Mui from "@mui/material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designType, designWeights } from "services/constants/designTokens";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { PayInputAuditTrail } from "pages/PayRoll/PayInputs/Shared/AuditTrail";
import { SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PF_BASIS_OPTIONS, ROUNDING_OPTIONS, VERSION_STATUS_COLORS, displayFormula, isLocked, versionRange } from "./pfConfigData";

// Version History (B3.2): every version with its range, status and the
// payrolls that used it, then what changed from one version to the next
// in the shared Activity Log format.

const yesNo = (value) => (value ? "Yes" : "No");
const optionLabel = (options, value) => options.find((option) => option.value === value)?.label || value || "–";
const FIELDS = [
  ["wage_ceiling", "PF wage ceiling (₹)"],
  ["eps_cap", "EPS wage cap (₹)"],
  ["eps_stop_age", "Stop EPS at age"],
  ["pf_basis_default", "PF basis", (value) => optionLabel(PF_BASIS_OPTIONS, value)],
  ["vpf_enabled", "Allow VPF", yesNo],
  ["vpf_max_pct", "VPF max %"],
  ["vpf_on_actual_allowed", "Contribution on actual wage", yesNo],
  ["opt_out_allowed", "Allow opt-out", yesNo],
  ["challan_due_day", "Challan due day"],
  ["rounding", "Rounding", (value) => optionLabel(ROUNDING_OPTIONS, value)],
];

const changesBetween = (before, after) => {
  if (!before) return [];
  const changes = FIELDS.filter(([key]) => String(before[key]) !== String(after[key])).map(([key, label, show = (value) => value]) => ({
    label,
    from: show(before[key]),
    to: show(after[key]),
  }));
  after.components.forEach((component) => {
    const old = before.components.find((item) => item.code === component.code);
    if (!old) changes.push({ label: `${component.code} added`, value: displayFormula(component) });
    else if (old.formula !== component.formula) changes.push({ label: component.code, from: displayFormula(old), to: displayFormula(component) });
    else if (old.status !== component.status) changes.push({ label: component.code, from: old.status, to: component.status });
  });
  before.components
    .filter((component) => !after.components.some((item) => item.code === component.code))
    .forEach((component) => changes.push({ label: `${component.code} removed`, value: component.name }));
  return changes;
};

export const PfVersionHistoryPanel = ({ open, versions, selectedId, onClose, onOpenVersion }) => {
  const saved = versions.filter((version) => version.status !== "Draft");
  const draft = versions.find((version) => version.status === "Draft");
  const entries = saved
    .map((version, index) => ({
      id: version.id,
      title: `${version.name} · ${versionRange(version)}`,
      at: version.created_at,
      actor: { name: version.created_by || "–" },
      tags: [{ label: version.status, tone: version.status === "Active" ? "green" : "slate" }],
      changes: changesBetween(saved[index - 1], version),
      details: [{ label: "Used by payroll", value: (version.used_by || []).join(", ") || "Not used yet" }],
      note: version.description,
    }))
    .reverse();

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Version History"
      subtitle="PF settings by month. A version used by a payroll is locked."
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RequestInfoBanner body="The version in force for a payroll month is the one whose range contains the 1st of that month. Mid-month versions are not allowed." />
        <SimpleTable
          fit
          heads={[
            { label: "Version", width: 110 },
            { label: "Range", width: 200 },
            { label: "Status", width: 100 },
            { label: "Used by payroll", width: 180 },
            { label: "", width: 80 },
          ]}
        >
          {[...versions].reverse().map((version) => (
            <Mui.TableRow key={version.id} selected={version.id === selectedId}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{version.name}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{versionRange(version)}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={version.status} colorCode={VERSION_STATUS_COLORS[version.status]} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{(version.used_by || []).join(", ") || "Not used yet"}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <SecondaryButton onClick={() => onOpenVersion(version.id)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                  {isLocked(version) ? "View" : "Open"}
                </SecondaryButton>
              </Mui.TableCell>
            </Mui.TableRow>
          ))}
        </SimpleTable>
        {draft ? (
          <Mui.Typography sx={designType.helperText}>
            {draft.name} is a draft from {versionRange(draft).split(" →")[0]}. It shows here once it is saved.
          </Mui.Typography>
        ) : null}
        <Mui.Typography sx={{ ...designType.sectionHeading, pt: 1 }}>What changed</Mui.Typography>
        <PayInputAuditTrail entries={entries} />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
